/**
 * Labstep - Table Handles Plugin
 *
 * Based on BlockNote's table handles implementation for improved UX
 */

import type { Node as ProsemirrorNode } from 'prosemirror-model';
import {
  Plugin,
  PluginKey,
  type PluginView,
} from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';

export interface TableHandlesState {
  show: boolean;
  showAddOrRemoveRowsButton: boolean;
  showAddOrRemoveColumnsButton: boolean;
  referencePosCell: DOMRect | undefined;
  referencePosTable: DOMRect;
  tableNode: ProsemirrorNode | undefined;
  colIndex: number | undefined;
  rowIndex: number | undefined;
  // New properties for BlockNote-style handles
  hoveredCell?: { row: number; col: number };
  tablePos?: number;
  draggingState?: {
    draggedCellOrientation: 'row' | 'col';
    originalIndex: number;
    mousePos: number;
  };
  widgetContainer: HTMLElement | undefined;
}

class TableHandlesView implements PluginView {
  public state?: TableHandlesState;
  public emitUpdate: () => void;

  public tableId: string | undefined;
  public tablePos: number | undefined;
  public tableElement: HTMLElement | undefined;

  public menuFrozen = false;
  public mouseState: 'up' | 'down' | 'selecting' = 'up';

  constructor(
    private readonly view: EditorView,
    emitUpdate: (state: TableHandlesState) => void,
  ) {
    this.emitUpdate = () => {
      if (!this.state) {
        throw new Error(
          'Attempting to update uninitialized table handles',
        );
      }
      emitUpdate(this.state);
    };

    // Set up event listeners
    this.view.dom.addEventListener(
      'mousemove',
      this.mouseMoveHandler,
    );
    this.view.dom.addEventListener(
      'mousedown',
      this.viewMousedownHandler,
    );
    window.addEventListener('mouseup', this.mouseUpHandler);
  }

  viewMousedownHandler = () => {
    this.mouseState = 'down';
  };

  mouseUpHandler = () => {
    this.mouseState = 'up';
  };

  mouseMoveHandler = (event: MouseEvent) => {
    if (this.menuFrozen) {
      return;
    }

    if (this.mouseState === 'selecting') {
      return;
    }

    if (
      !(event.target instanceof Element) ||
      !this.view.dom.contains(event.target)
    ) {
      // Hide handles when mouse is outside editor
      if (this.state?.show) {
        this.state.show = false;
        this.state.showAddOrRemoveRowsButton = false;
        this.state.showAddOrRemoveColumnsButton = false;
        this.emitUpdate();
      }
      return;
    }

    // Find the table cell around the target
    const target = this.domCellAround(event.target);

    if (
      target?.type === 'cell' &&
      this.mouseState === 'down' &&
      !this.state?.draggingState
    ) {
      // Hide handles when selecting text
      this.mouseState = 'selecting';

      if (this.state?.show) {
        this.state.show = false;
        this.state.showAddOrRemoveRowsButton = false;
        this.state.showAddOrRemoveColumnsButton = false;
        this.emitUpdate();
      }
      return;
    }

    // TODO: Implement full mouse move logic
    // For now, let's implement basic table detection

    if (target?.type === 'cell') {
      const cellElement = target.domNode;
      const table = cellElement.closest('table');

      if (table) {
        // Find row and column indices
        const row = cellElement.closest('tr');
        const rows = Array.from(table.querySelectorAll('tr'));
        const rowIndex = rows.indexOf(row as HTMLTableRowElement);

        const cells = Array.from(
          row?.querySelectorAll('td, th') || [],
        );
        const colIndex = cells.indexOf(
          cellElement as HTMLTableCellElement,
        );

        // Get table position for reference
        const tableRect = table.getBoundingClientRect();

        // Find the ProseMirror table node and position
        const pos = this.view.posAtDOM(cellElement, 0);
        const resolvedPos = this.view.state.doc.resolve(pos);

        // Walk up the document tree to find the table node
        let tableNode: ProsemirrorNode | undefined;
        let tablePos: number | undefined;

        for (let depth = resolvedPos.depth; depth >= 0; depth--) {
          const node = resolvedPos.node(depth);
          if (node.type.name === 'table') {
            tableNode = node;
            tablePos = resolvedPos.before(depth);
            break;
          }
        }

        // Only show handles if this cell belongs to a ProseMirror table node.
        // If we didn't find a corresponding table node in the document tree, bail out.
        if (!tableNode) {
          if (this.state?.show) {
            this.state.show = false;
            this.state.showAddOrRemoveRowsButton = false;
            this.state.showAddOrRemoveColumnsButton = false;
            this.emitUpdate();
          }
          return;
        }

        // (Optional safety) Ensure the DOM node returned by ProseMirror for the table position
        // actually contains the hovered HTML <table>. This avoids reacting to stray tables
        // that might be inside widgets or other markup not representing a PM table node.
        if (tablePos !== undefined) {
          const pmTableDom = this.view.nodeDOM(
            tablePos,
          ) as HTMLElement | null;
          if (pmTableDom && !pmTableDom.contains(table)) {
            if (this.state?.show) {
              this.state.show = false;
              this.state.showAddOrRemoveRowsButton = false;
              this.state.showAddOrRemoveColumnsButton = false;
              this.emitUpdate();
            }
            return;
          }
        }

        // Update state to show handles
        this.state = {
          show: true,
          showAddOrRemoveRowsButton: true,
          showAddOrRemoveColumnsButton: true,
          referencePosCell: cellElement.getBoundingClientRect(),
          referencePosTable: tableRect,
          tableNode,
          colIndex,
          rowIndex,
          // New properties for BlockNote-style handles
          hoveredCell: { row: rowIndex, col: colIndex },
          tablePos,
          widgetContainer: this.view.dom, // Use the editor DOM as container
        };

        this.emitUpdate();
      }
    } else if (this.state?.show) {
      // Not directly over a cell. Before hiding, check if pointer is still within
      // the table bounds (including a small expansion) to allow native column resize
      // hotspots (which sit on cell borders and may have different targets) without flicker.
      try {
        const tableRect = this.state.referencePosTable;
        const buffer = 20; // px tolerance around table for resize gutters
        const withinTable =
          event.clientX >= tableRect.left - buffer &&
          event.clientX <= tableRect.right + buffer &&
          event.clientY >= tableRect.top - buffer &&
          event.clientY <= tableRect.bottom + buffer;
        if (withinTable) {
          // Keep state; don't hide so handles persist near edges
          return;
        }
      } catch (_) {
        // Fall through to hide if any error
      }
      this.state.show = false;
      this.state.showAddOrRemoveRowsButton = false;
      this.state.showAddOrRemoveColumnsButton = false;
      this.emitUpdate();
    }
    // This is a simplified version - the full implementation would:
    // 1. Find the table element
    // 1. Find the table element
    // 2. Calculate cell indices
    // 3. Update state for handle positioning
    // 4. Handle edge cases for extend buttons
  };

  private domCellAround(target: Element) {
    while (target && target !== this.view.dom) {
      if (target.tagName === 'TD' || target.tagName === 'TH') {
        return {
          type: 'cell' as const,
          domNode: target as HTMLElement,
          tbodyNode: target.closest('tbody') as HTMLElement,
        };
      }
      target = target.parentElement!;
    }
    return null;
  }

  update() {
    // Handle updates when table is modified
    // This would update handle positions and state
  }

  destroy() {
    this.view.dom.removeEventListener(
      'mousemove',
      this.mouseMoveHandler,
    );
    window.removeEventListener('mouseup', this.mouseUpHandler);
    this.view.dom.removeEventListener(
      'mousedown',
      this.viewMousedownHandler,
    );
  }
}

export const tableHandlesPluginKey = new PluginKey(
  'TableHandlesPlugin',
);

export class TableHandlesPlugin {
  private view: TableHandlesView | undefined;
  private updateCallbacks: Array<(state: TableHandlesState) => void> =
    [];

  public onUpdate(callback: (state: TableHandlesState) => void) {
    this.updateCallbacks.push(callback);
    return () => {
      const index = this.updateCallbacks.indexOf(callback);
      if (index > -1) {
        this.updateCallbacks.splice(index, 1);
      }
    };
  }

  public createPlugin() {
    return new Plugin({
      key: tableHandlesPluginKey,
      view: (editorView) => {
        this.view = new TableHandlesView(editorView, (state) => {
          this.updateCallbacks.forEach((callback) => callback(state));
        });
        return this.view;
      },
    });
  }

  // Table manipulation methods
  public rowDragStart = (event: {
    dataTransfer: DataTransfer | null;
    clientY: number;
  }) => {
    // TODO: Implement row drag start
  };

  public colDragStart = (event: {
    dataTransfer: DataTransfer | null;
    clientX: number;
  }) => {
    // TODO: Implement column drag start
  };

  public dragEnd = () => {
    // TODO: Implement drag end
  };

  public freezeHandles = () => {
    if (this.view) {
      this.view.menuFrozen = true;
    }
  };

  public unfreezeHandles = () => {
    if (this.view) {
      this.view.menuFrozen = false;
    }
  };
}
